import { graphicAccentColors, normalizeGraphicData } from "./graphicLayer.js";
function listPlan(data, theme) {
    const plan = { rects: [], lines: [], circles: [], icons: [], texts: [] };
    const colors = graphicAccentColors(theme, data.items.length);
    const rowH = 1 / Math.max(data.items.length, 1);
    data.items.forEach((item, i) => {
        const y = i * rowH + rowH * 0.12;
        const iconR = rowH * 0.22;
        plan.circles.push({
            cx: 0.08,
            cy: y + rowH * 0.28,
            r: iconR,
            fill: colors[i],
        });
        plan.icons.push({
            cx: 0.08,
            cy: y + rowH * 0.28,
            r: iconR * 0.55,
            icon: item.icon ?? "dot",
            fill: theme.palette.surface,
            itemIndex: i,
        });
        plan.texts.push({
            x: 0.16,
            y: y + rowH * 0.12,
            w: 0.8,
            text: item.label,
            subtext: item.sublabel,
            fill: theme.palette.text,
            subFill: theme.palette.muted,
            size: rowH * 0.28,
            subSize: rowH * 0.2,
            itemIndex: i,
            field: "label",
        });
    });
    return plan;
}
function processPlan(data, theme) {
    const plan = { rects: [], lines: [], circles: [], icons: [], texts: [] };
    const colors = graphicAccentColors(theme, data.items.length);
    const n = data.items.length;
    const stepW = 1 / Math.max(n, 1);
    data.items.forEach((item, i) => {
        const cx = stepW * i + stepW * 0.5;
        const cy = 0.42;
        const r = Math.min(stepW, 0.12) * 0.45;
        plan.circles.push({ cx, cy, r, fill: colors[i] });
        plan.icons.push({ cx, cy, r: r * 0.55, icon: item.icon ?? "dot", fill: theme.palette.surface, itemIndex: i });
        plan.texts.push({
            x: cx - stepW * 0.4,
            y: 0.58,
            w: stepW * 0.8,
            text: item.label,
            subtext: item.sublabel,
            fill: theme.palette.text,
            subFill: theme.palette.muted,
            size: 0.05,
            subSize: 0.035,
            anchor: "middle",
            itemIndex: i,
            field: "label",
        });
        if (i < n - 1) {
            plan.lines.push({
                x1: cx + r,
                y1: cy,
                x2: cx + stepW - r,
                y2: cy,
                stroke: theme.palette.muted,
                width: 2,
            });
        }
    });
    return plan;
}
function cyclePlan(data, theme) {
    const plan = { rects: [], lines: [], circles: [], icons: [], texts: [] };
    const colors = graphicAccentColors(theme, data.items.length);
    const n = data.items.length;
    const cx = 0.5;
    const cy = 0.48;
    const radius = 0.32;
    data.items.forEach((item, i) => {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const px = cx + radius * Math.cos(angle);
        const py = cy + radius * Math.sin(angle);
        const r = 0.06;
        plan.circles.push({ cx: px, cy: py, r, fill: colors[i] });
        plan.icons.push({ cx: px, cy: py, r: r * 0.55, icon: item.icon ?? "dot", fill: theme.palette.surface, itemIndex: i });
        plan.texts.push({
            x: px - 0.12,
            y: py + r + 0.02,
            w: 0.24,
            text: item.label,
            fill: theme.palette.text,
            size: 0.04,
            anchor: "middle",
            itemIndex: i,
            field: "label",
        });
        if (i > 0) {
            const prev = ((i - 1) / n) * Math.PI * 2 - Math.PI / 2;
            plan.lines.push({
                x1: cx + radius * 0.85 * Math.cos(prev),
                y1: cy + radius * 0.85 * Math.sin(prev),
                x2: cx + radius * 0.85 * Math.cos(angle),
                y2: cy + radius * 0.85 * Math.sin(angle),
                stroke: theme.palette.muted,
                width: 1.5,
            });
        }
    });
    return plan;
}
function pyramidPlan(data, theme) {
    const plan = { rects: [], lines: [], circles: [], icons: [], texts: [] };
    const colors = graphicAccentColors(theme, data.items.length);
    let row = 0;
    let idx = 0;
    while (idx < data.items.length) {
        const count = row + 1;
        const rowY = 0.12 + row * 0.18;
        const blockW = 0.8 / count;
        for (let c = 0; c < count && idx < data.items.length; c += 1, idx += 1) {
            const item = data.items[idx];
            const x = 0.1 + c * blockW + blockW * 0.05;
            plan.rects.push({
                x,
                y: rowY,
                w: blockW * 0.9,
                h: 0.14,
                fill: colors[idx],
                rx: 0.01,
            });
            plan.texts.push({
                x,
                y: rowY + 0.02,
                w: blockW * 0.9,
                text: item.label,
                subtext: item.sublabel,
                fill: theme.palette.surface,
                subFill: theme.palette.surface,
                size: 0.045,
                subSize: 0.03,
                anchor: "middle",
                itemIndex: idx,
                field: "label",
            });
        }
        row += 1;
    }
    return plan;
}
function comparisonPlan(data, theme) {
    const plan = { rects: [], lines: [], circles: [], icons: [], texts: [] };
    const mid = Math.ceil(data.items.length / 2);
    const left = data.items.slice(0, mid);
    const right = data.items.slice(mid);
    const colW = 0.46;
    const renderCol = (items, x0, accent, indexOffset) => {
        const rowH = 1 / Math.max(items.length, 1);
        items.forEach((item, i) => {
            const y = i * rowH + 0.06;
            const globalIndex = indexOffset + i;
            plan.rects.push({ x: x0, y, w: colW, h: rowH * 0.82, fill: accent, rx: 0.01 });
            plan.texts.push({
                x: x0 + 0.02,
                y: y + 0.04,
                w: colW - 0.04,
                text: item.label,
                subtext: item.sublabel,
                fill: theme.palette.text,
                subFill: theme.palette.muted,
                size: rowH * 0.22,
                subSize: rowH * 0.16,
                itemIndex: globalIndex,
            });
        });
    };
    renderCol(left, 0.02, `${theme.palette.accent}33`, 0);
    renderCol(right, 0.52, `${theme.palette.muted}55`, mid);
    return plan;
}
const BUILDERS = {
    list: listPlan,
    process: processPlan,
    cycle: cyclePlan,
    pyramid: pyramidPlan,
    comparison: comparisonPlan,
};
export function buildGraphicRenderPlan(data, theme) {
    const normalized = normalizeGraphicData(data);
    return BUILDERS[normalized.template](normalized, theme);
}
